import React, { useEffect, useState } from "react";
import {
  Box, Typography, TextField, Button, Grid,
  FormControl, FormLabel, RadioGroup, FormControlLabel, Radio,
  InputLabel, Select, MenuItem, OutlinedInput,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useIntl } from "react-intl";

import {
  createInformationCapitalGoal,
  createInformationCapitalIssue,
  fetchDataTypes,
  fetchBusinessProcesses,
  fetchStrategicGoals,
  fetchCustomerValues,
  fetchFinancialGoals,
  fetchStrategicIssues,
  fetchHumanCapitalGoals,
  fetchInfoCapitalGoalsApp,
  fetchInfoCapitalGoalsIt,
  fetchInfoCapitalGoalsData,
  fetchInfoCapitalIssuesData,
} from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import { useAuth } from "../../providers/AuthProvider";

const GOAL_TYPE = "InformationCapitalGoalData";
const ISSUE_TYPE = "InformationCapitalIssueData";

export default function SmInfoCapitalGoalDataCreatePage() {
  const { businessDomainId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const { companyId } = useAuth();
  const t = (id) => intl.formatMessage({ id });

  const [kpiType, setKpiType] = useState("goal"); // "goal" | "issue"
  const [successFactorName, setSuccessFactorName] = useState("");
  const [successFactorDescription, setSuccessFactorDescription] = useState("");
  const [selectedDataType, setSelectedDataType] = useState(null);
  const [selectedNames, setSelectedNames] = useState([]);
  const [sourceIds, setSourceIds] = useState([]);
  const [selectedBusinessProcessId, setSelectedBusinessProcessId] = useState("");
  const [businessProcesses, setBusinessProcesses] = useState([]);
  const [dataTypes, setDataTypes] = useState([]);
  const [strategicGoals, setStrategicGoals] = useState([]);
  const [customerValues, setCustomerValues] = useState([]);
  const [financialGoals, setFinancialGoals] = useState([]);
  const [strategicIssues, setStrategicIssues] = useState([]);
  const [humanCapitalGoals, setHumanCapitalGoals] = useState([]);
  const [infoCapitalGoalsApp, setInfoCapitalGoalsApp] = useState([]);
  const [infoCapitalGoalsIt, setInfoCapitalGoalsIt] = useState([]);
  const [infoCapitalGoalsData, setInfoCapitalGoalsData] = useState([]);
  const [infoCapitalIssuesData, setInfoCapitalIssuesData] = useState([]);
  const [usedIndicatorNames, setUsedIndicatorNames] = useState(new Set());
  const [issueSelectedIndicatorNames, setIssueSelectedIndicatorNames] = useState([]);

  // 指標は選択されたデータタイプの indicators から取得
  const sourceIndicators = selectedDataType?.indicators || [];

  // 原因目標モード：選択したビジネスプロセスの指標
  const selectedBusinessProcess = businessProcesses.find((bp) => bp.businessProcessId === selectedBusinessProcessId) || null;
  const issueSourceIndicators = selectedBusinessProcess?.indicators || [];

  useEffect(() => {
    (async () => {
      try {
        const [dtypes, bps, sgGoals, cvGoals, fgGoals, issues, hcGoals, icApp, icIt, existingSfs, icIssuesData] = await Promise.all([
          fetchDataTypes(businessDomainId),
          fetchBusinessProcesses(businessDomainId).catch(() => []),
          fetchStrategicGoals(businessDomainId).catch(() => []),
          fetchCustomerValues(businessDomainId).catch(() => []),
          fetchFinancialGoals(businessDomainId).catch(() => []),
          fetchStrategicIssues(businessDomainId).catch(() => []),
          fetchHumanCapitalGoals(businessDomainId).catch(() => []),
          fetchInfoCapitalGoalsApp(businessDomainId).catch(() => []),
          fetchInfoCapitalGoalsIt(businessDomainId).catch(() => []),
          fetchInfoCapitalGoalsData(businessDomainId).catch(() => []),
          fetchInfoCapitalIssuesData(businessDomainId).catch(() => []),
        ]);
        setDataTypes(Array.isArray(dtypes) ? dtypes : []);
        setBusinessProcesses(Array.isArray(bps) ? bps : []);
        setStrategicGoals(Array.isArray(sgGoals) ? sgGoals : []);
        setCustomerValues(Array.isArray(cvGoals) ? cvGoals : []);
        setFinancialGoals(Array.isArray(fgGoals) ? fgGoals : []);
        setStrategicIssues(Array.isArray(issues) ? issues : []);
        setHumanCapitalGoals(Array.isArray(hcGoals) ? hcGoals : []);
        setInfoCapitalGoalsApp(Array.isArray(icApp) ? icApp : []);
        setInfoCapitalGoalsIt(Array.isArray(icIt) ? icIt : []);
        setInfoCapitalGoalsData(Array.isArray(existingSfs) ? existingSfs : []);
        setInfoCapitalIssuesData(Array.isArray(icIssuesData) ? icIssuesData : []);
        const used = new Set(
          (Array.isArray(existingSfs) ? existingSfs : [])
            .flatMap((sf) => sf.indicators || [])
            .map((ind) => ind.indicatorName)
            .filter(Boolean)
        );
        setUsedIndicatorNames(used);
      } catch (e) {
        notify.apiError(e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessDomainId]);

  const handleKpiTypeChange = (e) => {
    setKpiType(e.target.value);
    setSelectedDataType(null);
    setSelectedNames([]);
    setSourceIds([]);
    setSelectedBusinessProcessId("");
    setIssueSelectedIndicatorNames([]);
  };

  const handleBpChange = (e) => {
    setSelectedBusinessProcessId(e.target.value);
    setIssueSelectedIndicatorNames([]);
  };

  const handleDataTypeChange = (e) => {
    const type = dataTypes.find((dt) => dt.dataTypeId === e.target.value);
    setSelectedDataType(type || null);
    setSelectedNames([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (kpiType === "issue") {
        const allGoals = [...infoCapitalGoalsData, ...infoCapitalIssuesData];
        const issueSources = sourceIds
          .map((id) => {
            const goal = allGoals.find((s) => s.successFactorId === id);
            if (!goal) return null;
            return { successFactorId: goal.successFactorId, successFactorName: goal.successFactorName };
          })
          .filter(Boolean);
        const issueIndicators = issueSelectedIndicatorNames
          .map((name) => issueSourceIndicators.find((ind) => ind.indicatorName === name))
          .filter(Boolean);
        await createInformationCapitalIssue({
          successFactorName,
          successFactorDescription,
          businessDomainId,
          businessProcessId: selectedBusinessProcessId || undefined,
          successFactorType: ISSUE_TYPE,
          sources: issueSources,
          indicators: issueIndicators,
        });
      } else {
        if (sourceIndicators.length > 0 && selectedNames.length === 0) {
          notify.error(t("financialGoal.indicatorRequired"));
          return;
        }
        const indicators = selectedNames
          .map((name) => sourceIndicators.find((ind) => ind.indicatorName === name))
          .filter(Boolean);
        const allGoals = [...strategicIssues];
        const sources = sourceIds
          .map((id) => {
            const goal = allGoals.find((s) => s.successFactorId === id);
            if (!goal) return null;
            return { successFactorId: goal.successFactorId, successFactorName: goal.successFactorName };
          })
          .filter(Boolean);
        await createInformationCapitalGoal({
          successFactorName,
          successFactorDescription,
          dataTypeId: selectedDataType?.dataTypeId || "",
          indicators,
          sources,
          businessDomainId,
          companyId,
          successFactorType: GOAL_TYPE,
        });
      }
      notify.success(t("message.create.success"));
      navigate(`/design/${businessDomainId}/strategy-map/info-capital-goals-data`, { replace: true });
    } catch (err) {
      notify.apiError(err);
    }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {t("infoCapitalGoalData.create.title")}
      </Typography>

      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid size={12}>
            <FormControl component="fieldset">
              <FormLabel component="legend">KPI種類</FormLabel>
              <RadioGroup row value={kpiType} onChange={handleKpiTypeChange}>
                <FormControlLabel value="goal" control={<Radio />} label="結果目標" />
                <FormControlLabel value="issue" control={<Radio />} label="原因目標" />
              </RadioGroup>
            </FormControl>
          </Grid>

          <Grid size={12}>
            <TextField
              fullWidth required
              label={t("successFactor.name")}
              value={successFactorName}
              onChange={(e) => setSuccessFactorName(e.target.value)}
            />
          </Grid>

          <Grid size={12}>
            <TextField
              fullWidth multiline rows={3}
              label={t("successFactor.description")}
              value={successFactorDescription}
              onChange={(e) => setSuccessFactorDescription(e.target.value)}
            />
          </Grid>

          {kpiType === "issue" && <Grid size={12}>
            <FormControl fullWidth>
              <InputLabel>{t("businessProcess.title")}</InputLabel>
              <Select
                value={selectedBusinessProcessId}
                label={t("businessProcess.title")}
                onChange={handleBpChange}
              >
                <MenuItem value=""><em>—</em></MenuItem>
                {businessProcesses.map((bp) => (
                  <MenuItem key={bp.businessProcessId} value={bp.businessProcessId}>
                    {bp.businessProcessName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>}

          {kpiType === "goal" && <Grid size={12}>
            <FormControl fullWidth>
              <InputLabel>{t("dataType.label")}</InputLabel>
              <Select
                value={selectedDataType?.dataTypeId || ""}
                label={t("dataType.label")}
                onChange={handleDataTypeChange}
              >
                <MenuItem value=""><em>—</em></MenuItem>
                {dataTypes.map((dt) => (
                  <MenuItem key={dt.dataTypeId} value={dt.dataTypeId}>
                    {dt.dataTypeName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>}

          {/* ソース選択（結果目標モード：全種類） */}
          {kpiType === "goal" && <Grid size={12}>
            <FormControl fullWidth>
              <InputLabel>{t("orgCapitalGoal.sources.label")}</InputLabel>
              <Select
                multiple
                value={sourceIds}
                onChange={(e) => setSourceIds(e.target.value)}
                input={<OutlinedInput label={t("orgCapitalGoal.sources.label")} />}
              >
                {strategicIssues.length > 0 && [
                  <MenuItem key="__si_header" disabled sx={{ fontWeight: 700, opacity: 1, color: "text.primary" }}>
                    ── {t("strategicIssue.list.title")} ──
                  </MenuItem>,
                  ...strategicIssues.map((item) => (
                    <MenuItem key={item.successFactorId} value={item.successFactorId}>
                      {item.successFactorName}
                    </MenuItem>
                  )),
                ]}
              </Select>
            </FormControl>
          </Grid>}

          {/* ソース選択（原因目標モード：情報資本目標のみ） */}
          {kpiType === "issue" && <Grid size={12}>
            <FormControl fullWidth>
              <InputLabel>{t("orgCapitalGoal.sources.label")}</InputLabel>
              <Select
                multiple
                value={sourceIds}
                onChange={(e) => setSourceIds(e.target.value)}
                input={<OutlinedInput label={t("orgCapitalGoal.sources.label")} />}
              >
                {infoCapitalGoalsData.length > 0 && [
                  <MenuItem key="__icgd_header" disabled sx={{ fontWeight: 700, opacity: 1, color: "text.primary" }}>
                    ── {t("infoCapitalGoalData.list.title")} ──
                  </MenuItem>,
                  ...infoCapitalGoalsData.map((item) => (
                    <MenuItem key={item.successFactorId} value={item.successFactorId}>
                      {item.successFactorName}
                    </MenuItem>
                  )),
                ]}
                {infoCapitalIssuesData.length > 0 && [
                  <MenuItem key="__icid_header" disabled sx={{ fontWeight: 700, opacity: 1, color: "text.primary" }}>
                    ── {t("infoCapitalIssueData.list.title")} ──
                  </MenuItem>,
                  ...infoCapitalIssuesData.map((item) => (
                    <MenuItem key={item.successFactorId} value={item.successFactorId}>
                      {item.successFactorName}
                    </MenuItem>
                  )),
                ]}
              </Select>
            </FormControl>
          </Grid>}

          {/* 指標選択（結果目標モード） */}
          {kpiType === "goal" && <Grid size={12}>
            <FormControl fullWidth>
              <InputLabel>{t("financialGoal.selectIndicatorHint")}</InputLabel>
              <Select
                multiple
                disabled={sourceIndicators.length === 0}
                value={selectedNames}
                onChange={(e) => setSelectedNames(e.target.value)}
                input={<OutlinedInput label={t("financialGoal.selectIndicatorHint")} />}
              >
                {sourceIndicators.map((ind) => (
                  <MenuItem key={ind.indicatorName} value={ind.indicatorName} disabled={usedIndicatorNames.has(ind.indicatorName)}>
                    {ind.indicatorName}（{ind.unit}）{usedIndicatorNames.has(ind.indicatorName) ? " （登録済み）" : ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>}

          {/* 指標選択（原因目標モード） */}
          {kpiType === "issue" && <Grid size={12}>
            <FormControl fullWidth>
              <InputLabel>{t("financialGoal.selectIndicatorHint")}</InputLabel>
              <Select
                multiple
                disabled={issueSourceIndicators.length === 0}
                value={issueSelectedIndicatorNames}
                onChange={(e) => setIssueSelectedIndicatorNames(e.target.value)}
                input={<OutlinedInput label={t("financialGoal.selectIndicatorHint")} />}
              >
                {issueSourceIndicators.map((ind) => (
                  <MenuItem key={ind.indicatorName} value={ind.indicatorName} disabled={usedIndicatorNames.has(ind.indicatorName)}>
                    {ind.indicatorName}（{ind.unit}）{usedIndicatorNames.has(ind.indicatorName) ? " （登録済み）" : ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>}
        </Grid>

        <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
          <Button variant="contained" type="submit" disabled={!successFactorName.trim()}>
            {t("common.add")}
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate(`/design/${businessDomainId}/strategy-map/info-capital-goals-data`)}
          >
            {t("common.cancel")}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
